import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { CreatorAction, PartnershipTierComponent } from "@/types/types";
import { effectiveCpm, formatCpm } from "@/lib/cpm";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import {
  CheckCircle,
  Check,
  X,
  Instagram,
  Music,
  Mail,
  MessageCircle,
  Phone,
  Send,
  Copy,
  ExternalLinkIcon,
} from "lucide-react";
import { useDashboardStore, useEveActionsStore } from "@/stores";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { ConfirmPopover, ConfirmPopoverLink } from "@/components/ui/confirm-popover";
import { Link } from "react-router-dom";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Frame,
  FrameHeader,
  FramePanel,
  FrameTitle,
  FrameAction,
} from "@/components/ui/frame";
import { ActivateCreatorDialogView } from "@/components/creators-table/actions/ActivateCreatorDialog";
import { formatCurrency } from "@/lib/format";

interface WaitingApprovalActionProps {
  action: CreatorAction;
}

/** Label/value rows for one tier component of the closed deal. */
function offerFacts(
  component: PartnershipTierComponent,
  action: CreatorAction
): { label: string; value: string }[] {
  const negotiatedPrice = action.negotiated_price;
  const money = (n: number | null | undefined) => (n != null ? formatCurrency(n, action.currency) : "—");
  const price = money(negotiatedPrice);
  const facts: { label: string; value: string }[] = [];
  switch (component.type) {
    case "max_budget":
      facts.push({ label: "Agreed price", value: price });
      break;
    case "custom_formula":
      facts.push({ label: "Agreed price", value: price });
      if (component.custom_formula?.expression) {
        facts.push({
          label: "Formula",
          value: component.custom_formula.expression,
        });
      }
      break;
    case "max_cpm": {
      // The deal's real rate — the configured ceiling already reads out of
      // the component description ("Up to $X Max CPM …") below.
      const cpm = effectiveCpm(
        negotiatedPrice,
        action.median_views,
        action.number_of_deliverables
      );
      facts.push(
        { label: "Agreed price", value: price },
        { label: "CPM rate", value: cpm != null ? formatCpm(cpm, action.currency) : "—" }
      );
      break;
    }
    case "cpa":
      facts.push({
        label: "CPA",
        value: money(component.value),
      });
      break;
    case "revenue_share":
      facts.push({
        label: "Revenue share",
        value: component.value != null ? `${component.value}%` : "—",
      });
      break;
    case "performance_cpm": {
      // The FROZEN per-creator terms (exact numbers quoted in the deal);
      // falls back to the generic component description pre-freeze.
      const terms = action.performance_terms;
      if (terms?.rates?.length) {
        for (const rate of terms.rates) {
          facts.push({
            label: `CPM above ${rate.above_views.toLocaleString("en-US")} views`,
            value: `${money(rate.cpm)} / 1,000`,
          });
        }
        facts.push({
          label: "Payout cap",
          value: money(terms.cap),
        });
        facts.push({
          label: "Measured",
          value: `${terms.window_months} month${terms.window_months === 1 ? "" : "s"} after posting`,
        });
        return facts;
      }
      return [{ label: "Performance bonus", value: component.description || "—" }];
    }
    default:
      // "other" components carry their whole offer in the description.
      return [{ label: "Other incentive", value: component.description || "—" }];
  }
  if (component.description) {
    facts.push({ label: "Description", value: component.description });
  }
  return facts;
}

/**
 * Right-column value, mirroring the campaign-settings InfoItem: long values
 * truncate behind a fade mask and hover-underline into a card with the full
 * text and a copy button.
 */
function OfferValue({ value }: { value: string }) {
  if (value === "—") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (value.length <= 30) {
    return <span className="wrap-break-word">{value}</span>;
  }
  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger className="block cursor-default hover:underline data-[state=open]:underline underline-offset-2 max-w-[200px] overflow-hidden whitespace-nowrap mask-[linear-gradient(to_right,black_70%,transparent)] truncate">
        {value}
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        className="w-auto max-w-80 bg-muted dark:bg-sidebar p-0.5 rounded-lg gap-0 border-0"
      >
        <p className="text-xs wrap-break-word p-2.5 bg-background rounded-md border">
          {value}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function WaitingApprovalAction({
  action,
}: WaitingApprovalActionProps) {
  const { getToken } = useAuth();
  const { setCreatorStatus, dubDomains } = useDashboardStore();
  const { approveCreator, rejectCreator } = useEveActionsStore();
  const [approveOpen, setApproveOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Eve-run campaigns route decisions to the amy action endpoints — the
  // legacy updateCreatorStatus 409s them as a backstop.
  const isEve = action.is_eve === true;

  const tiers = action.partnership?.tiers ?? [];
  const closedTier = tiers.find(
    (t) => t.order === (action.current_tier_order ?? 1)
  );

  const removeTaskRow = () =>
    useDashboardStore.setState((state) => ({
      tasks: state.tasks.filter((task) => task._id !== action._id),
    }));

  const handleReject = async () => {
    if (submitting) return;
    setSubmitting(true);
    const token = await getToken();
    if (token) {
      if (isEve) {
        const ok = await rejectCreator(action._id, token);
        if (ok) removeTaskRow();
      } else {
        await setCreatorStatus(
          action._id,
          "pending-rejection",
          { useDub: action.use_dub },
          token
        );
      }
    }
    setSubmitting(false);
  };

  const renderFieldValue = (
    type: string | undefined,
    entry?: { value?: string; accepted?: boolean }
  ) => {
    if (type === "boolean") {
      if (entry?.accepted === true) {
        return (
          <span className="flex items-center gap-1 text-success">
            <CheckCircle className="size-3.5" /> Accepted
          </span>
        );
      }
      if (entry?.accepted === false) {
        return (
          <span className="flex items-center gap-1 text-destructive">
            <X className="size-3.5" /> Rejected
          </span>
        );
      }
      return <span className="text-muted-foreground">—</span>;
    }
    return entry?.value ? (
      <span className="wrap-break-word">{entry.value}</span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  };

  const getSocialUrl = (platform: string, handle: string) => {
    switch (platform.toLowerCase()) {
      case "instagram":
        return `https://www.instagram.com/${handle}`;
      case "tiktok":
        return `https://www.tiktok.com/@${handle}`;
      case "youtube":
        return `https://www.youtube.com/channel/${handle}`;
      case "email":
        return `mailto:${handle}`;
      case "whatsapp":
        return `https://wa.me/${handle.replace(/\D/g, "")}`;
      case "telegram":
        return handle.startsWith("t.me/") ? `https://${handle}` : `https://t.me/${handle}`;
      default:
        return null;
    }
  };

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "instagram":
        return <Instagram className="size-4" />;
      case "tiktok":
        return <Music className="size-4" />;
      case "email":
        return <Mail className="w-4 h-4" />;
      case "whatsapp":
        return <Phone className="w-4 h-4" />;
      case "telegram":
        return <Send className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const handleSocialClick = async (platform: string, handle: string) => {
    if (platform.toLowerCase() === "email") {
      try {
        await navigator.clipboard.writeText(handle);
        toast.success("Email copied to clipboard!");
      } catch {
        toast.error("Failed to copy email");
      }
    } else {
      const url = getSocialUrl(platform, handle);
      if (url) {
        window.open(url, "_blank");
      }
    }
  };

  return (
    <div className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      {closedTier && (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Agreed Offer</FrameTitle>
            <FrameAction>
              <span className="text-muted-foreground font-mono uppercase text-[11px]">
                Tier {closedTier.order}
                {tiers.length > 1 ? ` of ${tiers.length}` : ""}
              </span>
            </FrameAction>
          </FrameHeader>
          <FramePanel className="flex flex-col divide-y p-0 overflow-hidden">
            {closedTier.components.flatMap((component, i) =>
              offerFacts(component, action).map((fact) => (
                <div
                  key={`${i}-${fact.label}`}
                  className="flex items-center justify-between gap-4 p-4 py-3 hover:bg-muted group"
                >
                  <span className="text-xs text-muted-foreground">
                    {fact.label}
                  </span>
                  <div className="flex items-center gap-2 text-sm font-medium text-right wrap-break-word">
                    <CopyButton
                      buttonText=""
                      textToCopy={fact.value === "—" ? "" : fact.value}
                      tooltipText={`Copy ${fact.label}`}
                      tooltipSide={"left"}
                      className="size-7 [&_svg]:size-3.5 rounded-md opacity-0 group-hover:opacity-100 transition-none"
                      variant="secondary"
                    />
                    <OfferValue value={fact.value} />
                  </div>
                </div>
              ))
            )}
          </FramePanel>
        </Frame>
      )}

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Decision</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex w-full gap-2 p-3">
          <Button
            variant="success-ghost"
            className="flex-1"
            disabled={submitting}
            onClick={() => setApproveOpen(true)}
          >
            {submitting ? <Spinner /> : <Check />}
            Approve
          </Button>
          <ConfirmPopover
            title="Send Rejection Message?"
            action={
              <ConfirmPopoverLink asChild>
                <Link to={`/dashboard/campaigns/${action.campaign_id}/brief/rejection_message`}>Edit</Link>
              </ConfirmPopoverLink>
            }
            description='Amy sends rejection message and moves creator to "Rejected" status.'
            confirmLabel={<><X /> Reject Creator</>}
            confirmVariant="destructive"
            onConfirm={handleReject}
          >
            <Button
              variant="destructive-ghost"
              className="flex-1"
              disabled={submitting}
            >
              {submitting ? <Spinner /> : <X />}
              Reject
            </Button>
          </ConfirmPopover>
        </FramePanel>
      </Frame>

      <ActivateCreatorDialogView
        open={approveOpen}
        onOpenChange={setApproveOpen}
        username={action.username}
        useDub={(action.use_dub ?? false) && !isEve}
        ccEmails={action.cc_emails ?? []}
        currentTierOrder={action.current_tier_order ?? 1}
        tiers={action.partnership?.tiers ?? []}
        negotiatedPrice={action.negotiated_price}
        currency={action.currency}
        dubDomains={dubDomains}
        campaignId={action.campaign_id}
        checkFunding={isEve}
        onConfirm={async ({
          promoCode,
          manualUniqueLink,
          selectedDomain,
          extraVariables,
        }) => {
          const token = await getToken();
          if (token) {
            if (isEve) {
              const ok = await approveCreator(
                action._id,
                { promo_code: promoCode, unique_link: manualUniqueLink },
                token
              );
              if (ok) removeTaskRow();
            } else {
              await setCreatorStatus(
                action._id,
                "pending-activation",
                {
                  promoCode,
                  manualUniqueLink,
                  selectedDomain,
                  useDub: action.use_dub,
                  extraVariables,
                },
                token
              );
            }
          }
        }}
      />

      {(action.campaign_fields?.length ?? 0) > 0 && (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Collected Information</FrameTitle>
          </FrameHeader>
          <FramePanel className="flex flex-col divide-y p-0 overflow-hidden">
            {action.campaign_fields!.map((def) => {
              const entry = action.fields?.find((f) => f.name === def.name);
              return (
                <div
                  key={def.name}
                  className="flex items-center justify-between gap-4 p-4 py-3 hover:bg-muted group"
                >
                  <span className="text-xs text-muted-foreground capitalize">
                    {def.name}
                  </span>
                  <div className="flex items-center gap-2 text-sm font-medium text-right wrap-break-word">
                    <CopyButton
                      buttonText=""
                      textToCopy={entry?.value || ""}
                      tooltipText={`Copy ${def.name}`}
                      tooltipSide={"left"}
                      className="size-7 [&_svg]:size-3.5 rounded-md opacity-0 group-hover:opacity-100 transition-none"
                      variant="secondary"
                    />
                    {renderFieldValue(def.type, entry)}
                    {entry?.use_default && (
                      <span className="text-xs text-muted-foreground">
                        (default)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </FramePanel>
        </Frame>
      )}

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Creator's Social</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex flex-col divide-y p-0 overflow-hidden">
          {action.social &&
            Object.entries(action.social)
              .filter(([, handle]) => Boolean(handle))
              .map(([platform, handle]) => (
              <Button
                key={platform}
                variant="ghost"
                scope={false}
                className="w-full justify-between transition-none h-auto gap-3 rounded-none first:rounded-t-lg last:rounded-b-lg text-start"
                onClick={() => handleSocialClick(platform, handle as string)}
              >
                <div className="size-8 rounded-pill bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                  {getSocialIcon(platform)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium capitalize">{platform}</p>
                  <p className="text-xs opacity-60 font-normal">
                    {platform === "email" || platform === "whatsapp" || String(handle).includes("/") ? handle : `@${handle}`}
                  </p>
                </div>
                <div className="text-muted-foreground">
                  {platform === "email" ? (
                    <Copy className="size-4" />
                  ) : (
                    <ExternalLinkIcon className="size-4" />
                  )}
                </div>
              </Button>
            ))}
        </FramePanel>
      </Frame>
    </div>
  );
}
